import { css } from '@emotion/react';
import React, { useState, useContext, useRef } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { AngularContext } from 'react-app';
import { getUserTimezone } from 'redux/selectors/users';
import { wrapThunkAction } from 'redux/utils';
import { useAppDispatch } from 'redux/store';
import { updateUser } from 'redux/actions/users';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import useTrapFocus from 'shared/hooks/use-trap-focus';
import NvIcon from 'shared/components/nv-icon';
import NvTimezoneSelector from 'shared/components/nv-timezone-selector';
import { primary } from 'styles/global_defaults/colors';
import { config } from '../../../../config/pendo.config.json';

const ACCOUNT_SETTINGS_LINK = '#!/account_settings/language_and_timezone';

const styles = css`
  .selector-container {
    border: 1px solid rgba(29, 33, 38, 0.1);
    border-radius: 6px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  }

  .dropdown-container {
    width: 300px;

    .bs4-dropdown-menu {
      overflow-y: auto;
    }
  }

  .more-settings-link {
    &:hover, &:focus {
      color: ${primary};
    }
  }
`;

const TimezoneSelector = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const currentUserTimezone = useSelector(getUserTimezone);
  const timezoneSelectorRef = useRef(null);

  const [selectedTimezone, setSelectedTimezone] = useState(currentUserTimezone);

  useTrapFocus(timezoneSelectorRef);

  const handleUpdate = () => {
    // Logic to update the timezone
    const timeZone = TimezonesService.timezones.find((tz) => tz.longName === selectedTimezone);
    wrapThunkAction(
      dispatch(updateUser({ timeZone: timeZone.shortName })),
    ).then(() => {
      // Refresh the page, so that everything is displayed in the new timezone
      window.location.reload();
    }).catch((error) => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    });
  };

  return (
    <div css={styles} className='react-app' ref={timezoneSelectorRef}>
      <div className='selector-container p-4 bg-white d-flex flex-column align-items-center'>
        <div className='label gray-2 w-100 text-left'>
          {t.ACCOUNT_SETTINGS.LANGS_AND_TZ.TIME_ZONE_HEADER()}:
        </div>
        <div className='mt-2 dropdown-container'>
          <NvTimezoneSelector
            defaultTimezone={selectedTimezone}
            onchange={(newTimezone) => setSelectedTimezone(newTimezone.longName)}
          />
        </div>
        <div className='mt-4'>
          <Button
            size='sm'
            className='text-small'
            data-qa={config.pendo.lhs.timezoneClock.moreSettings}
            onClick={handleUpdate}
            disabled={selectedTimezone === currentUserTimezone}
          >
            {t.FORM.UPDATE()}
          </Button>
        </div>
        <div className='mt-4'>
          <a
            className='more-settings-link label color-primary d-flex align-items-center'
            href={ACCOUNT_SETTINGS_LINK}
            target='_blank'
            rel='noopener noreferrer'
            data-qa={config.pendo.lhs.timezoneClock.moreSettings}
          >
            {t.ACCOUNT_SETTINGS.LANGS_AND_TZ.MORE_SETTINGS()}
            <NvIcon icon='open-external' size='xss-smallest' className='ml-1' />
          </a>
        </div>
      </div>
    </div>
  );
};

export default TimezoneSelector;
